/* This is hack to work around layout problems related to:
  - scrolling and url bar height change at that time
  - bottom of browsers being ignored and viewport height calculated including space it takes
  (elements appear covered by it)

  More details see here:
  https://css-tricks.com/the-trick-to-viewport-units-on-mobile/

  IMPORTANT: when using vh units, for layout to render correctly you need to multiply them by var(--vh, 1vh);
  E.g: height: calc(var(--vh, 1vh) * 100);
*/

import { useLayoutEffect } from 'react';
import { isIOSThemeEnabled } from '../../IOSTheme/IOSTheme';
import { IOS_WRAPPER_LAYOUT_CUTOFF } from '../../IOSTheme/useIOSLayoutFix';

const useMobileViewportUnitsFix = (): void => {
  const setViewportUnits = () => {
    // the trick for viewport units on mobile
    // https://css-tricks.com/the-trick-to-viewport-units-on-mobile/
    const windowInnerHeight = getWindowInnerHeight();
    const vh = windowInnerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  };

  const listenToOrientationChange = (mediaQueryList: MediaQueryList) => {
    try {
      mediaQueryList.addEventListener('change', setViewportUnits);
    } catch {
      // iOS 13 does not support addEventListener
      // details https://github.com/mdn/sprints/issues/858#issuecomment-515238645
      mediaQueryList.addListener(setViewportUnits);
    }
  };

  const removeOrientationChangeListener = (mediaQueryList: MediaQueryList) => {
    try {
      mediaQueryList.removeEventListener('change', setViewportUnits);
    } catch {
      // iOS 13 does not support removeEventListener
      // details https://github.com/mdn/sprints/issues/858#issuecomment-515238645
      mediaQueryList.removeListener(setViewportUnits);
    }
  };

  useLayoutEffect(() => {
    setViewportUnits();

    const mediaQueryList = window.matchMedia('(orientation: portrait)');
    window.addEventListener('resize', setViewportUnits, {
      capture: true,
      passive: true,
    });
    listenToOrientationChange(mediaQueryList);

    return () => {
      window.removeEventListener('resize', setViewportUnits);
      removeOrientationChangeListener(mediaQueryList);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // eslint disabled to run the layout effect once
};

// In iOS wrapper, to hide status bar out layout is extended by 20px and moved up by 20px
// Like this <rect key="frame" x="0.0" y="-20" width="1024" height="788"/>
// So we have use this hack in order for web app layout not to be cutoff by 20px on top
const getWindowInnerHeight = () =>
  isIOSThemeEnabled()
    ? window.innerHeight - IOS_WRAPPER_LAYOUT_CUTOFF
    : window.innerHeight;

export default useMobileViewportUnitsFix;
